<template>
  <div>
    <v-row no-gutters class="mb-8 px-8">

      <v-col class="pt-8 px-lg-16">

        <!-- Page title -->
        <v-row class="d-flex pb-8">
          <v-col class="d-flex justify-center text-h3 font-weight-light text-center">
            {{ isOperationMode ? $t('menus.reportsOperation') : $t('menus.rfqs') }}
          </v-col>
        </v-row>

        <!-- Toolbar -->
        <reports-toolbar
          :date-start.sync="dateStart"
          :date-end.sync="dateEnd"
          :date-filter-enabled="true"
          :filter-value.sync="selectedFilterId"
          :filter-items="filterItems"
          filter-item-text="name"
          filter-item-value="id"
          :filter-label="$t('reports.filterByClient')"
          :side-filter.sync="selectedSideFilter"
          :status-filter.sync="selectedStatusFilter"
          :status-filter-items="statusFilterItems"
          :status-filter-label="$t('reports.filterByStatus')"
          :is-broker="isBroker"
          :is-partner="isPartner"
          :locale="locale"
          @expand-all="expandAll"
          @contract-all="contractAll"
          @reset-filter="resetFilter"
          @export-pdf="exportPDF"
          @export-csv="exportCSV"
        />

        <!-- Data table -->
        <v-row class="d-flex">
          <v-col>
            <component
              :is="tableComponent"
              :items="filteredData"
              :expanded.sync="expanded"
              :page.sync="tablePage"
              :items-per-page.sync="itemsPerPage"
              :is-broker="isBroker"
              :is-partner="isPartner"
              :is-pro="isPro"
              :locale="locale"
              @export-single-pdf="exportSinglePDF"
              @export-single-csv="exportSingleCSV"
              @documents-updated="fetchReports"
            />
          </v-col>
        </v-row>

      </v-col>
    </v-row>
  </div>
</template>

<script>
import { resolveUserRole } from '@/utils.js'
import { mapActions } from 'pinia'
import { useExecutionStore, useAuthStore, useSnackbarStore } from '@/store/index'

import { generateRfqsReportPDF } from '@/exportRfqsReportToPDF.js'
import { generateRFQCSV, generateOPCSV } from '@/exportReportsToCSV.js'
import { generateOperationsPDF } from '@/exportOperationsToPDF.js'
import { getDefaultReportDateRange, normalizeOperationReportRow, normalizeRfqReportRow, translateStatus, createReportDateComparator } from '@/components/reports/reportHelpers.js'

import ReportsToolbar         from '@/components/reports/ReportsToolbar.vue'
import ReportsRfqTable        from '@/components/reports/ReportsRfqTable.vue'
import ReportsOperationTable  from '@/components/reports/ReportsOperationTable.vue'

export default {
  name: 'ReportsView',

  components: {
    ReportsToolbar,
    ReportsRfqTable,
    ReportsOperationTable,
  },

  data () {
    const defaultDates = getDefaultReportDateRange()
    return {
      expanded:           [],
      tablePage:          1,
      itemsPerPage:       10,
      dateStart:          defaultDates.start,
      dateEnd:            defaultDates.end,
      isBroker:           false,
      isPartner:          false,
      isNotPro:           false,
      rawData:            [],
      selectedFilterId:   null,
      selectedSideFilter: 'BOTH',
      selectedStatusFilter: null,
      suppressDateWatcher: false,
    }
  },

  computed: {
    isOperationMode () {
      return this.$route.name === 'operation-reports'
    },
    isPro () {
      return !this.isBroker && !this.isPartner
    },
    tableComponent () {
      return this.isOperationMode ? 'ReportsOperationTable' : 'ReportsRfqTable'
    },
    statusField () {
      return this.isOperationMode ? 'status' : 'rfq_status'
    },
    locale () {
      return localStorage.getItem('userLanguage') || 'en'
    },
    filterItems () {
      const seen = new Map()
      for (const row of this.rawData) {
        const c = row.company
        if (c && c.id && !seen.has(c.id)) {
          seen.set(c.id, { id: c.id, name: c.name || c.id })
        }
      }
      return Array.from(seen.values())
    },
    statusFilterItems () {
      const seen = new Map()
      for (const row of this.rawData) {
        const status = row[this.statusField]
        if (status && !seen.has(status)) {
          seen.set(status, { value: status, label: translateStatus(status) })
        }
      }
      return Array.from(seen.values())
    },
    filteredData () {
      if (!this.rawData || !this.rawData.length) return []

      let filtered = this.rawData
      if (this.selectedFilterId) {
        filtered = filtered.filter(r => r.company && r.company.id === this.selectedFilterId)
      }

      if (this.selectedSideFilter && this.selectedSideFilter !== 'BOTH') {
        const sideField = this.isOperationMode ? 'side' : 'rfq_side'
        filtered = filtered.filter(r => r[sideField] === this.selectedSideFilter)
      }

      if (this.selectedStatusFilter) {
        filtered = filtered.filter(r => r[this.statusField] === this.selectedStatusFilter)
      }

      return filtered
    },
  },

  watch: {
    '$route.name' () {
      this.resetFilter(false)
      this.expanded = []
      this.tablePage = 1
      this.rawData = []
      this.fetchReports()
    },
    dateEnd () {
      if (this.suppressDateWatcher) return
      this.normalizeDateRange('end')
      this.fetchReports()
    },
    dateStart () {
      if (this.suppressDateWatcher) return
      this.normalizeDateRange('start')
      this.fetchReports()
    },
  },

  async mounted () {
    this.getUserRole()
    await this.fetchReports()
    this.applyRouteRfqExpand()
    this.applyRouteOperationExpand()
  },

  methods: {
    ...mapActions(useExecutionStore, ['getRfqReports', 'getOperationReports']),
    ...mapActions(useAuthStore, ['getUserRoleFromLocalStorage']),

    getUserRole () {
      const role = this.getUserRoleFromLocalStorage()
      const flags = resolveUserRole(role)
      this.isBroker  = flags.isBroker
      this.isPartner = flags.isPartner
      this.isNotPro  = flags.isNotPro
    },

    async fetchReports () {
      const brokerId = localStorage.getItem('brokerId')
      if (!brokerId) return

      try {
        const params = {
          broker_id: brokerId,
          date_min:  this.dateStart,
          date_max:  this.dateEnd,
        }

        const companyId = localStorage.getItem('companyId')
        if (companyId && companyId !== 'null') {
          params.company_id = companyId
        }

        if (this.isOperationMode) {
          const res = await this.getOperationReports(params)
          const rawRows = res?.data?.reports ?? []
          this.rawData = rawRows.map(r => this.transformOperationRow(r))
        } else {
          const res = await this.getRfqReports(params)
          const rawRows = res.data.reports || []
          this.rawData = rawRows.map(row => normalizeRfqReportRow(row))
        }

        // PRO clients (not broker/partner) must only ever see their own company's
        // reports — enforce this client-side too, don't rely solely on the backend filter.
        if (this.isNotPro && companyId && companyId !== 'null') {
          this.rawData = this.rawData.filter(row => row.company?.id === companyId)
        }

        if (this.selectedFilterId && !this.filterItems.find(f => f.id === this.selectedFilterId)) {
          this.selectedFilterId = null
        }
        if (this.selectedStatusFilter && !this.statusFilterItems.find(s => s.value === this.selectedStatusFilter)) {
          this.selectedStatusFilter = null
        }
      } catch (error) {
        console.error('[ReportsView] fetchReports:', error)
      }
    },

    transformOperationRow (r) {
      return normalizeOperationReportRow(r)
    },

    normalizeDateRange (changedField) {
      if (!this.dateEnd || !this.dateStart) return
      if (this.dateStart > this.dateEnd) {
        if (changedField === 'start') {
          this.dateEnd = this.dateStart
        } else {
          this.dateStart = this.dateEnd
        }
      }
    },

    resetFilter (shouldFetch = true) {
      this.selectedFilterId   = null
      this.selectedSideFilter = 'BOTH'
      this.selectedStatusFilter = null
      this.tablePage = 1
      const defaultDates = getDefaultReportDateRange()
      this.suppressDateWatcher = true
      this.dateStart = defaultDates.start
      this.dateEnd = defaultDates.end
      this.$nextTick(() => {
        this.suppressDateWatcher = false
        if (shouldFetch) this.fetchReports()
      })
    },

    async exportPDF () {
      try {
        if (this.isOperationMode) await generateOperationsPDF(this.filteredData)
        else await generateRfqsReportPDF(this.filteredData)
        useSnackbarStore().open(this.$t('pdf.exportSuccess'), 'success')
      } catch (e) {
        console.error('[ReportsView] exportPDF:', e)
        useSnackbarStore().open(this.$t('pdf.exportError'), 'error')
      }
    },
    exportCSV () {
      if (this.isOperationMode) generateOPCSV(this.filteredData, 'operations')
      else generateRFQCSV(this.filteredData, 'rfqs')
    },
    async exportSinglePDF (item) {
      try {
        if (this.isOperationMode) await generateOperationsPDF([item])
        else await generateRfqsReportPDF([item])
        useSnackbarStore().open(this.$t('pdf.exportSuccess'), 'success')
      } catch (e) {
        console.error('[ReportsView] exportSinglePDF:', e)
        useSnackbarStore().open(this.$t('pdf.exportError'), 'error')
      }
    },
    exportSingleCSV (item) {
      if (this.isOperationMode) generateOPCSV([item], `operation_${item.id}`)
      else generateRFQCSV([item])
    },

    expandAll ()   { this.expanded = this.filteredData },
    contractAll () { this.expanded = [] },

    applyRouteRfqExpand () {
      const rfqId = this.$route.query.rfq_id
      if (rfqId && !this.isOperationMode) {
        const match = this.rawData.find(row => String(row.rfq_id) === String(rfqId))
        if (match) {
          this.expanded = [match]
          const compareDate = createReportDateComparator()
          const sorted = [...this.filteredData].sort((a, b) => compareDate(b.timecreate, a.timecreate))
          const index = sorted.findIndex(row => String(row.rfq_id) === String(rfqId))
          if (index !== -1) {
            this.tablePage = Math.floor(index / this.itemsPerPage) + 1
          }
        }
      }
      if (rfqId) {
        const { rfq_id, ...rest } = this.$route.query
        this.$router.replace({ query: rest }).catch(() => {})
      }
    },

    applyRouteOperationExpand () {
      const opId = this.$route.query.operation_id
      if (opId && this.isOperationMode) {
        const match = this.rawData.find(row => String(row.id) === String(opId))
        if (match) {
          this.expanded = [match]
          const compareDate = createReportDateComparator()
          const sorted = [...this.filteredData].sort((a, b) => compareDate(b.timecreate, a.timecreate))
          const index = sorted.findIndex(row => String(row.id) === String(opId))
          if (index !== -1) {
            this.tablePage = Math.floor(index / this.itemsPerPage) + 1
          }
        }
      }
      if (opId) {
        const { operation_id, ...rest } = this.$route.query
        this.$router.replace({ query: rest }).catch(() => {})
      }
    },
  },
}
</script>
